import { useQuery, type QueryClient, type UseQueryResult } from '@tanstack/react-query';
import { METHOD, sendRequest } from '@/state/http-request.helpers.ts';
import { useAuth } from '@clerk/clerk-react';
import type { GetPublicationStateResponseDto } from '@/types/dtos/events/response/publication-state.dto';

export const PUBLICATION_STATE_QUERY_KEY = 'publication-state';

/** Invalidate all publication-state queries (any event / mode). Call after edits that change publishable content. */
export function invalidatePublicationStateQueries(queryClient: QueryClient): Promise<void> {
  return queryClient.invalidateQueries({ queryKey: [PUBLICATION_STATE_QUERY_KEY] });
}

export type PublicationStateMode = 'PLAN' | 'ORGANIZE' | 'PERFORM';

export type UsePublicationStateQueryParams = {
  eventId: string | undefined;
  mode?: PublicationStateMode;
  /**
   * When false, the query does not run. Pass `false` if the user lacks event `update` (same bar as batch publish).
   * @default true
   */
  enabled?: boolean;
};

export type PublicationStateQueryResult = UseQueryResult<GetPublicationStateResponseDto, Error> & {
  /** True when the query ran successfully and returned data (staleness flags are authoritative). */
  isPublicationStateReady: boolean;
  /** True while enabled and waiting for the first successful response (or after error, until a success). */
  isPublicationStateLoading: boolean;
};

export function usePublicationStateQuery(params: UsePublicationStateQueryParams): PublicationStateQueryResult {
  const { eventId, mode = 'ORGANIZE', enabled: enabledOption = true } = params;
  const { getToken } = useAuth();

  const enabled = Boolean(eventId) && enabledOption;

  const query = useQuery({
    queryKey: [PUBLICATION_STATE_QUERY_KEY, eventId, mode],
    queryFn: async () => {
      const token = await getToken();
      return fetchPublicationState({ eventId: eventId!, mode, token: token ?? '' });
    },
    enabled,
    // Server recomputes isUpToDate on every GET; keep cache fresh when mutations invalidate.
    staleTime: 0,
  });

  const isPublicationStateReady = enabled && query.isSuccess && Boolean(query.data);
  const isPublicationStateLoading = enabled && !query.isError && !query.isSuccess;

  return {
    ...query,
    isPublicationStateReady,
    isPublicationStateLoading,
  };
}

async function fetchPublicationState(options: {
  eventId: string;
  mode: PublicationStateMode;
  token: string;
}): Promise<GetPublicationStateResponseDto> {
  const { eventId, mode, token } = options;
  const url = `${import.meta.env.VITE_API_URL}/events/${eventId}/publication-state?mode=${encodeURIComponent(mode)}`;
  const response = await sendRequest({ url, method: METHOD.GET, token });
  return response.json() as Promise<GetPublicationStateResponseDto>;
}
